import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Shield, AlertTriangle, X } from 'lucide-react';
import { gorunurAralik } from '../utils/gorunurAralik';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin için cihaz onay + güvenlik olayı bekleyenleri gösteren bell badge.
 * Sidebar veya header'a yerleştirilir. 60 saniyede bir refresh eder.
 */
export default function DeviceTrustBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ kritik: 0, uyari: 0, bilgi: 0, items: [] });
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/admin/guvenlik-olaylari?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const olaylar = (r.data.olaylar || []).filter(o => !o.resolved);
      setData({
        kritik: olaylar.filter(o => o.severity === 'kritik').length,
        uyari: olaylar.filter(o => o.severity === 'uyari').length,
        bilgi: olaylar.filter(o => o.severity === 'bilgi').length,
        items: olaylar.slice(0, 8)
      });
    } catch (err) {
      // sessizce başarısız ol — sidebar'ı kırmamak için
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
    return gorunurAralik(fetchData, 60000);  // 60 sn, yalnız görünür sekmede
  }, [fetchData]);

  const toplam = data.kritik + data.uyari + data.bilgi;
  if (toplam === 0) return null;  // hiç bekleyen yok → gösterme

  const severityColor = (s) => ({
    kritik: 'bg-red-500',
    uyari: 'bg-amber-500',
    bilgi: 'bg-blue-500'
  }[s] || 'bg-gray-500');

  const eventTypeLabel = (t) => ({
    esamanli_farkli_ip: '🚨 Eşzamanlı IP',
    ip_kume_sapmasi: '⚠️ IP Sapması',
    ip_sik_degisim: '⚠️ Sık IP Değişimi',
    yeni_cihaz_onay_bekliyor: '🆕 Yeni Cihaz Onayı',
    musteri_sapheli_bildirim: '🚨 Şüpheli Bildirim',
    admin_otp_goruntuledi: '👁 OTP Görüntülendi',
    admin_direkt_otp_uretti: '⚡ Direkt Kod Üretildi'
  }[t] || t);

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)}
        data-testid="device-trust-bell-btn"
        title={`${toplam} bekleyen güvenlik olayı`}
        className="relative p-2 hover:bg-white/10 rounded-lg transition group">
        <Shield className="text-yellow-300" size={20} />
        {toplam > 0 && (
          <span className={`absolute -top-1 -right-1 text-[10px] font-bold text-white rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 ${data.kritik > 0 ? 'bg-red-500 animate-pulse' : data.uyari > 0 ? 'bg-amber-500' : 'bg-blue-500'}`}>
            {toplam > 99 ? '99+' : toplam}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 flex items-center justify-between">
              <div className="flex items-center">
                <Shield size={18} className="mr-2" />
                <span className="font-bold">Güvenlik Bildirimleri</span>
              </div>
              <button onClick={() => setOpen(false)} className="hover:bg-white/20 p-1 rounded">
                <X size={16} />
              </button>
            </div>

            <div className="flex divide-x text-center text-xs">
              <div className="flex-1 p-2 bg-red-50">
                <div className="font-bold text-red-700">{data.kritik}</div>
                <div className="text-red-600">Kritik</div>
              </div>
              <div className="flex-1 p-2 bg-amber-50">
                <div className="font-bold text-amber-700">{data.uyari}</div>
                <div className="text-amber-600">Uyarı</div>
              </div>
              <div className="flex-1 p-2 bg-blue-50">
                <div className="font-bold text-blue-700">{data.bilgi}</div>
                <div className="text-blue-600">Bilgi</div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-2">
              {loading && (
                <div className="text-center py-4 text-gray-500 text-sm">Yükleniyor...</div>
              )}
              {!loading && data.items.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <AlertTriangle className="mx-auto mb-2 text-gray-300" size={24} />
                  <p className="text-sm">Bekleyen olay yok</p>
                </div>
              )}
              {data.items.map(o => (
                <button key={o.id}
                  onClick={() => {
                    if (o.musteri?.id) {
                      navigate(`/panel/musteri/${o.musteri.id}/cihazlar`);
                    } else {
                      navigate('/panel/guvenlik-olaylari');
                    }
                    setOpen(false);
                  }}
                  className="w-full text-left p-2.5 hover:bg-gray-50 rounded-lg border-b last:border-b-0 text-sm transition">
                  <div className="flex items-start gap-2">
                    <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${severityColor(o.severity)}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 truncate">{eventTypeLabel(o.event_type)}</div>
                      {o.musteri && (
                        <div className="text-xs text-gray-600 truncate">
                          {o.musteri.cari_unvan || o.musteri.email}
                          {o.musteri.firma && <span className="ml-1 text-gray-400">· {o.musteri.firma}</span>}
                          {o.musteri.panel_kodu && <span className="ml-1 font-mono text-indigo-600">· No: {o.musteri.panel_kodu}</span>}
                        </div>
                      )}
                      <div className="text-[10px] text-gray-400">
                        {o.created_at ? new Date(o.created_at).toLocaleString('tr-TR') : '-'}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <button onClick={() => { navigate('/panel/guvenlik-olaylari'); setOpen(false); }}
              className="w-full p-3 bg-gray-50 hover:bg-gray-100 text-sm font-semibold text-indigo-600 border-t">
              Tümünü Görüntüle →
            </button>
          </div>
        </>
      )}
    </div>
  );
}
